/**
 * Month calendar grid: ‹ Month YYYY › header, Su–Sa initials, then 4–6
 * Sunday-start week rows from getMonthMatrix. Day cells copy WeekStrip's
 * treatment exactly — today = focus-color ring; selected = translucent blue
 * fill with interactive.primaryHover ink (NOT onColor: the fill is
 * translucent; NOT text.link: fails 4.5:1 on the tint in light); the
 * selected day hides its dot because its list renders below. Adjacent-month
 * cells dim their number but stay tappable, and dots render on them too.
 */
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import {
  formatDayAccessibilityLabel,
  formatMonthLabel,
  getMonthMatrix,
  isSameMonth,
  toDateKey,
} from '@/utils/residents/todoCalendar';

const WEEKDAY_INITIALS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface MonthGridProps {
  /** Any date inside the visible month. */
  monthAnchor: Date;
  selectedKey: string;
  todayKey: string;
  countsByKey: Record<string, number>;
  onSelectDay: (d: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

export const MonthGrid: React.FC<MonthGridProps> = React.memo(
  ({
    monthAnchor,
    selectedKey,
    todayKey,
    countsByKey,
    onSelectDay,
    onPrevMonth,
    onNextMonth,
  }) => {
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();
    const styles = useThemedStyles(({ colors, semantic }) =>
      StyleSheet.create({
        container: {
          gap: space.sm,
        },
        headerRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        arrow: {
          paddingHorizontal: space.md,
          paddingVertical: space.xs,
          // A single chevron glyph + these paddings lands ~29pt tall; pin the
          // 44pt a11y floor and center the glyph inside the grown box.
          minHeight: 44,
          minWidth: 44,
          alignItems: 'center',
          justifyContent: 'center',
        },
        arrowText: {
          ...type.cardTitleLarge,
          color: colors.text.secondary,
        },
        monthLabel: {
          ...type.cardTitle,
          color: colors.text.primary,
        },
        weekdaysRow: {
          flexDirection: 'row',
          gap: space.xs,
        },
        weekdayLabel: {
          flex: 1,
          textAlign: 'center',
          ...type.captionMedium,
          color: semantic.text.secondary,
        },
        weekRow: {
          flexDirection: 'row',
          gap: space.xs,
        },
        dayCell: {
          flex: 1,
          // Number + dot alone come in under the 44pt a11y floor.
          minHeight: 44,
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: space.xs,
          borderRadius: radius.md,
          borderWidth: 1,
          borderColor: 'transparent',
          gap: space.xs,
        },
        dayCellToday: {
          borderColor: semantic.border.focus,
        },
        dayCellSelected: {
          backgroundColor: colors.accent.blueBgStrong,
          borderColor: 'transparent',
        },
        dayNumber: {
          ...type.cardTitle,
          color: colors.text.secondary,
        },
        dayNumberOutside: {
          color: colors.text.muted,
        },
        dayNumberToday: {
          color: colors.text.primary,
        },
        dayNumberSelected: {
          color: semantic.interactive.primaryHover,
        },
        dot: {
          width: 5,
          height: 5,
          borderRadius: radius.full,
          backgroundColor: semantic.status.info,
        },
        dotSpacer: {
          width: 5,
          height: 5,
        },
      }),
    );

    const weeks = useMemo(() => getMonthMatrix(monthAnchor), [monthAnchor]);

    return (
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={styles.arrow}
            onPress={onPrevMonth}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Previous month"
          >
            <Text style={styles.arrowText}>‹</Text>
          </TouchableOpacity>
          <Text style={styles.monthLabel}>{formatMonthLabel(monthAnchor)}</Text>
          <TouchableOpacity
            style={styles.arrow}
            onPress={onNextMonth}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Next month"
          >
            <Text style={styles.arrowText}>›</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.weekdaysRow}>
          {WEEKDAY_INITIALS.map(initial => (
            <Text key={initial} style={styles.weekdayLabel}>
              {initial}
            </Text>
          ))}
        </View>
        {weeks.map(week => (
          <View key={toDateKey(week[0])} style={styles.weekRow}>
            {week.map(day => {
              const key = toDateKey(day);
              const isSelected = key === selectedKey;
              const isToday = key === todayKey;
              const isOutside = !isSameMonth(day, monthAnchor);
              const count = countsByKey[key] ?? 0;
              const showDot = count > 0 && !isSelected;
              return (
                <TouchableOpacity
                  key={key}
                  style={[
                    styles.dayCell,
                    isToday && styles.dayCellToday,
                    isSelected && styles.dayCellSelected,
                  ]}
                  onPress={() => onSelectDay(day)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected }}
                  accessibilityLabel={formatDayAccessibilityLabel(day, count)}
                >
                  <Text
                    style={[
                      styles.dayNumber,
                      isOutside && styles.dayNumberOutside,
                      isToday && styles.dayNumberToday,
                      isSelected && styles.dayNumberSelected,
                    ]}
                  >
                    {String(day.getDate())}
                  </Text>
                  {showDot ? (
                    <View style={styles.dot} testID={`todo-dot-${key}`} />
                  ) : (
                    <View style={styles.dotSpacer} />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        ))}
      </View>
    );
  },
);

MonthGrid.displayName = 'MonthGrid';
